import React, { useContext, useEffect, useState } from "react";

import {
  Alert,
  Box,
  Grid,
  LinearProgress,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import clsx from "clsx";
import mainStyles from "styles";

import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";

import {
  getDocumentsAskAISettings,
  updateDocumentsAskAISettings,
} from "api/endpoints/documents-ai";
import { callOptionsHeaders } from "api/helpers";
import DisplayError from "components/shared/display-error";
import DisplayHeading from "components/shared/display-heading";
import { DEFAULT_SNACKBAR_DURATION } from "constants/index";
import { PermissionsContext } from "context/permissions-context";
import { GrpcError, SnackbarContext } from "context/snackbar-context";

interface OrgAITabProps {
  roleId: string;
  regionCode: string;
}

const TABLE_BORDER_STYLE = {
  borderWidth: "1px",
  borderStyle: "solid",
  borderRadius: "5px",
  margin: "20px",
} as const;

const LoadingStateTable = () => (
  <Table size="medium">
    <TableBody>
      <TableRow>
        <TableCell colSpan={2}>
          <LinearProgress />
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>
);

interface AISwitchCellProps {
  askAIEnabled: boolean;
  canUpdateAISettings: boolean;
  isUpdating: boolean;
  onToggle: (enabled: boolean) => void;
  classes: ReturnType<typeof mainStyles>;
}

const AISwitchCell = ({
  askAIEnabled,
  isUpdating,
  onToggle,
  canUpdateAISettings,
  classes,
}: AISwitchCellProps) => (
  <TableCell
    className={clsx(
      classes.tableCell,
      classes.leftCell,
      classes.tableRightColPadding
    )}
    align="left"
    style={{ fontWeight: "normal", width: "70%" }}
  >
    <Box display="flex" alignItems="center" gap={2}>
      <Switch
        checked={askAIEnabled}
        onChange={(event) => onToggle(event.target.checked)}
        disabled={!canUpdateAISettings || isUpdating}
        data-testid="documents-ask-ai-switch"
      />
      <Typography variant="body2" color="text.secondary">
        {askAIEnabled
          ? "Allow users to ask AI questions about documents. Also enables document ingestion."
          : "AI questions about documents are disabled"}
      </Typography>
    </Box>
  </TableCell>
);

const OrgAITab = ({ roleId, regionCode }: OrgAITabProps) => {
  const theme = useTheme();
  const classes = mainStyles();
  const { showError } = useContext(SnackbarContext);
  const { hasPermissions, permissions } = useContext(PermissionsContext);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [askAIEnabled, setAskAIEnabled] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [error, setError] = useState<string>("");
  const canUpdateAISettings =
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.UPDATE_DOCUMENTS_ASK_AI_SETTINGS]);

  const loadAISettings = async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await getDocumentsAskAISettings(
        { orgId: roleId },
        callOptionsHeaders(regionCode, roleId)
      );
      setAskAIEnabled(response.enabled);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(errorMessage);
      showError(err as GrpcError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleAskAI = async (enabled: boolean) => {
    setIsUpdating(true);
    setError("");

    try {
      await updateDocumentsAskAISettings(
        { orgId: roleId, enabled },
        callOptionsHeaders(regionCode, roleId)
      );
      setAskAIEnabled(enabled);
      setSuccessMessage(
        `AI Assistant has been ${enabled ? "enabled" : "disabled"}`
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(errorMessage);
      showError(err as GrpcError);
      // Revert the checkbox state on error
      setAskAIEnabled(!enabled);
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    loadAISettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid container>
      <Grid item xs={12} sx={classes.paddingTopSmall}>
        <DisplayHeading>AI SETTINGS</DisplayHeading>
        <DisplayError errorMessage={error} align="center" />
      </Grid>
      <Grid item xs={12} sx={classes.paddingTopSmall} />
      <Grid item xs={12}>
        <Box
          style={{
            borderColor: theme.palette.divider,
            ...TABLE_BORDER_STYLE,
          }}
        >
          {isLoading ? (
            <LoadingStateTable />
          ) : (
            <Table size="medium">
              <TableBody>
                <TableRow>
                  <TableCell
                    className={clsx(
                      classes.tableCell,
                      classes.leftCell,
                      classes.tableLeftColPadding
                    )}
                    align="right"
                    style={{ width: "30%" }}
                  >
                    <Typography variant="body1" fontWeight="medium">
                      AI Assistant
                    </Typography>
                  </TableCell>
                  <AISwitchCell
                    askAIEnabled={askAIEnabled}
                    canUpdateAISettings={canUpdateAISettings}
                    isUpdating={isUpdating}
                    onToggle={handleToggleAskAI}
                    classes={classes}
                  />
                </TableRow>
              </TableBody>
            </Table>
          )}
        </Box>
      </Grid>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={!!successMessage}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => setSuccessMessage("")}
        data-testid="ai-settings-success-snackbar"
      >
        <Alert severity="success" variant="filled">
          {successMessage}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default OrgAITab;
